import {Trans} from '@lingui/macro';
import React, {useMemo, useState} from 'react';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {FolderBreadcrumb} from '../../../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {ApiRuleBundle, RuleBundleModel, serializeRuleBundle} from '../../../../../models/api/ruleBundleModel';
import {deleteAsync, postApiAsync, putApiAsync} from '../../../../../services/http/http';
import {InterfaceEventsEnum} from '../../../../../services/tracking';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsWrapper,
} from '../../../../common/settingsWrapper';
import {CustomFields} from '../../../../editors/commons/customFieldsModel';
import {
  createRuleEditorContext,
  RuleEditorContextProvider,
} from '../../../../editors/ruleEditor/ruleEditorContext';
import {parseApiError, trackRuleEvent} from '../../../ruleHelpers';
import {RuleIntegration} from '../../../ruleIntegrationModel';
import {DeleteDialog} from '../../dialog/deleteDialog';
import {StyledRuleEditorWrapper, useRuleEditorErrorBanner} from '../../ruleEditor';
import {RuleEditorFooter} from '../../ruleEditorFooter';
import {RuleEditorHeader} from '../../ruleEditorHeader';
import {RuleEditorRuleNameInput} from '../../ruleEditorRuleNameInput';
import {useSlaEditorState} from './hooks/useSlaEditorState';
import {SlaEditorForm} from './slaEditorForm';
import {buildSlaBundleFromState} from './slaEditorHelpers';
import {SlaEditorSidebar} from './slaEditorSidebar';
import {getSlaEditorErrors, SlaTagsModels} from './state/slaEditorState';

/*
 * Props.
 */

interface SlaEditorProps {
  namespace: string;
  ruleIntegration: RuleIntegration;
  customFields: CustomFields;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  navigateToRules: Handler;
  ruleBundle?: RuleBundleModel;
  slaTags: SlaTagsModels;
}

/*
 * Component.
 */

export const SlaEditor: React.FC<SlaEditorProps> = (props) => {
  const {namespace, ruleIntegration, customFields, slaTags, ruleBundle, navigateToRules} = props;

  const {maybeRenderErrorBanner, setErrorBannerMessage} = useRuleEditorErrorBanner();
  const [shouldShowDeleteDialog, setShouldShowDeleteDialog] = useState(false);

  const company = useAppSelector(getCompany);

  const editorContext = useMemo(
    () =>
      createRuleEditorContext({
        namespace,
        hasExpressions: false,
        ruleIntegration,
        isLibrary: true,
        hasHighlightErrorsFlag: false,
        expressions: [],
        customFields,
      }),
    [namespace, ruleIntegration, customFields],
  );

  const {slaEditorState, ...actions} = useSlaEditorState(
    ruleBundle,
    customFields,
    ruleIntegration,
    slaTags,
    namespace,
  );

  const onCreate = async () => {
    const bundle = buildSlaBundleFromState(slaEditorState, slaTags, namespace);

    try {
      const newBundle = await postApiAsync(
        ApiRuleBundle,
        `${company.url}/namespaces/${namespace}/rule_bundles`,
        serializeRuleBundle(bundle),
      );

      trackEvents(newBundle, namespace, InterfaceEventsEnum.SETTINGS_RULE_CREATE);
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
      return;
    }
    navigateToRules();
  };

  const onUpdate = async () => {
    if (!ruleBundle) {
      return;
    }

    const bundle = buildSlaBundleFromState(slaEditorState, slaTags, namespace);

    try {
      const newBundle = await putApiAsync(
        ApiRuleBundle,
        `${company.url}/rule_bundles/${ruleBundle.id}`,
        serializeRuleBundle(bundle),
      );

      trackEvents(newBundle, namespace, InterfaceEventsEnum.SETTINGS_RULE_UPDATE);
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
      return;
    }
    navigateToRules();
  };

  const onDelete = async () => {
    if (!ruleBundle) {
      return;
    }

    try {
      await deleteAsync(`${company.url}/rule_bundles/${ruleBundle.id}`);
      trackEvents(ruleBundle, namespace, InterfaceEventsEnum.SETTINGS_RULE_DELETE);
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
      return;
    }
    navigateToRules();
  };

  const onDeleteWithDialog = () => {
    setShouldShowDeleteDialog(true);
  };

  const maybeRenderDeleteDialog = () => {
    if (!shouldShowDeleteDialog) {
      return null;
    }

    return <DeleteDialog onOk={onDelete} onCancel={() => setShouldShowDeleteDialog(false)} />;
  };

  const errors = getSlaEditorErrors(slaEditorState, editorContext.ruleIntegration);

  return (
    <RuleEditorContextProvider context={editorContext}>
      <StyledSettingsWrapper>
        <RuleEditorHeader
          isActive={slaEditorState.ruleState.isActive}
          namespace={namespace}
          isEditing={Boolean(ruleBundle)}
          isReadonly={editorContext.isReadOnly}
          title={<Trans id="sla-editor-title">SLA rule</Trans>}
          breadcrumbs={props.breadcrumbs}
          onUpdateIsActive={actions.onToggleIsActive}
          onDelete={onDeleteWithDialog}
        />
        <StyledSettingsMain>
          <StyledRuleEditorWrapper>
            <StyledSettingsContent>
              <RuleEditorRuleNameInput
                ruleName={slaEditorState.ruleState.name}
                onUpdateRuleName={actions.onUpdateRuleName}
              />
              <SlaEditorForm state={slaEditorState} actions={actions} namespace={namespace} />
              {maybeRenderErrorBanner()}
              {maybeRenderDeleteDialog()}
            </StyledSettingsContent>
          </StyledRuleEditorWrapper>
          <SlaEditorSidebar />
        </StyledSettingsMain>
        <RuleEditorFooter
          editorErrors={errors}
          isEditing={Boolean(ruleBundle)}
          onCreate={onCreate}
          onEdit={onUpdate}
          onCancel={navigateToRules}
        />
      </StyledSettingsWrapper>
    </RuleEditorContextProvider>
  );
};

/*
 * Helpers.
 */

function trackEvents(ruleBundle: RuleBundleModel, namespace: string, event: InterfaceEventsEnum) {
  ruleBundle.rules.forEach((rule) => {
    trackRuleEvent(event, rule, namespace, 'sla');
  });
}
